// The dated FX table (D6), and where new rates are added.
//
// It gets its own screen because it is the provenance of every converted number
// on the dashboard: the footnote under each report names a rate and an `as_of`,
// and this is where that row can be seen, added to, and — when it was simply
// wrong — removed.

import { useState, type ReactNode } from "react";
import type { FxRate } from "@crm/shared";
import styles from "./FxRatesPage.module.css";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card } from "../../design/Card.js";
import { Notice } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { formatCalendarDate, todayIsoDate } from "../../format/calendarDate.js";
import { formatRate } from "../../format/money.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import { useFxRateMutations } from "../../api/mutations.js";
import { useFxRates } from "../../api/queries.js";
import { useHasRole } from "../../auth/useAuth.js";
import { FxRateFormDialog } from "./FxRateFormDialog.js";

type FxAction =
  | { kind: "create" }
  | { kind: "edit"; rate: FxRate }
  | { kind: "delete"; rate: FxRate };

/** The newest row whose effective date has arrived, or null if none has. */
function inForceRate(rates: readonly FxRate[]): FxRate | null {
  const today = todayIsoDate();
  return rates.reduce<FxRate | null>(
    (best, rate) =>
      rate.effective_date <= today && (best === null || rate.effective_date > best.effective_date)
        ? rate
        : best,
    null,
  );
}

/**
 * Which rate a report would actually use. Without it the table is a list of
 * numbers with no indication of which one is live — and "dated, so history
 * doesn't restate" (D6) is only legible if you can see where today falls.
 */
function StandingBadge({ rate, inForceId }: { rate: FxRate; inForceId: string | null }): ReactNode {
  if (rate.id === inForceId) {
    return (
      <Badge shape="chip" tone="good">
        In force
      </Badge>
    );
  }
  if (rate.effective_date > todayIsoDate()) {
    return (
      <Badge shape="chip" tone="info">
        Scheduled
      </Badge>
    );
  }
  return (
    <Badge shape="chip" tone="neutral">
      Superseded
    </Badge>
  );
}

export function FxRatesPage(): ReactNode {
  const query = useFxRates();
  const mutations = useFxRateMutations();
  const canEdit = useHasRole("crm_editor");
  const [action, setAction] = useState<FxAction | null>(null);

  usePageChrome({
    title: "FX rates",
  });

  const columns = canEdit ? 4 : 3;

  return (
    <div className={styles.page}>
      {canEdit && (
        <div className={styles.toolbar}>
          <Button
            variant="primary"
            className={styles.addRate}
            onClick={() => setAction({ kind: "create" })}
          >
            {/* Decorative, so the accessible name stays the words alone. */}
            <span aria-hidden="true">+</span>
            Add rate
          </Button>
        </div>
      )}

      <QueryBoundary query={query} label="the FX rates">
        {(page) => {
          // The newest row dated on or before today is the one a report
          // converting "as of now" will pick. Resolved once for the whole
          // table, so the badge can only ever land on one row.
          const inForce = inForceRate(page.items);
          return (
            <>
              {page.items.length > 0 && (
                <Notice
                  tone="info"
                  title="The rate in force is the newest one dated on or before the period being reported — the same “latest row on or before” rule the agreement terms use."
                />
              )}

              <Card>
                <Table label="FX rates">
                  <thead>
                    <tr>
                      <Th>Effective</Th>
                      <Th numeric>USD → CAD</Th>
                      <Th>Standing</Th>
                      {canEdit && <Th />}
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.length === 0 ? (
                      <EmptyRow
                        colSpan={columns}
                        title="No FX rates"
                        description="Until there is a rate, any report asked for a currency it has to convert into fails with fx_rate_unavailable rather than guessing."
                      />
                    ) : (
                      page.items.map((rate) => (
                        <tr key={rate.id}>
                          <Td mono nowrap>
                            {formatCalendarDate(rate.effective_date)}
                          </Td>
                          <Td numeric mono>
                            {formatRate(rate.usd_to_cad)}
                          </Td>
                          <Td>
                            <StandingBadge rate={rate} inForceId={inForce?.id ?? null} />
                          </Td>
                          {canEdit && (
                            <Td>
                              <div className={styles.rowActions}>
                                <Button
                                  small
                                  variant="subtle"
                                  aria-label={`Edit the rate effective ${rate.effective_date}`}
                                  onClick={() => setAction({ kind: "edit", rate })}
                                >
                                  Edit
                                </Button>
                                <Button
                                  small
                                  variant="subtle"
                                  aria-label={`Delete the rate effective ${rate.effective_date}`}
                                  onClick={() => setAction({ kind: "delete", rate })}
                                >
                                  Delete
                                </Button>
                              </div>
                            </Td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Card>
            </>
          );
        }}
      </QueryBoundary>

      {action?.kind === "create" && <FxRateFormDialog onClose={() => setAction(null)} />}
      {action?.kind === "edit" && (
        <FxRateFormDialog rate={action.rate} onClose={() => setAction(null)} />
      )}
      {action?.kind === "delete" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete FX rate"
          description={`The rate effective ${action.rate.effective_date} will be removed. Reports covering that period will fall back to the previous rate, and their numbers will change.`}
          record={action.rate}
          remove={(id, version) => mutations.remove.mutateAsync({ id, version })}
          fetchCurrent={mutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
    </div>
  );
}
